import { Box, Drawer, IconButton } from "@mui/material";
import React, { useState } from "react";

import * as gendersServices from "../../services/gendersServices";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import { Close, DashboardCustomize, Logout, Person } from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";
import axios from "axios";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import AgeSelect from "../../screens/selects/AgeSelect";
import BasinSelect from "../../screens/selects/BasinSelect";
import CategorySelect from "../../screens/selects/CategorySelect";
import ExtractionSelect from "../../screens/selects/ExtractionSelect";
import FormationSelect from "../../screens/selects/FormationSelect";
import GenderSelect from "../../screens/selects/GenderSelect";
import { searchByGenders, searchDownload } from "../../services/samplesServices";
import { isLogged } from "../../state/auth/authSlice";
import { routes, utils } from "../../utils";
import { LocationsResults } from "../../utils/types";
import BasicInput from "../BasicInput";
import Button from "../Button";
import ConfirmDialog from "../ConfirmDialog";

const drawerWidth = 380;

interface Props {
	setMapLocations: (e?: any) => void;
	mapLocations: LocationsResults | undefined;
	tokenChecked: boolean;
}

const INITIAL_PARAMS = {
	date_from: null,
	date_to: null,
	extractions: [],
	ages: [],
	basins: [],
	genders: [],
	formations: [],
	categories: [],
};

const buttonStyle = {
	backgroundColor: "white",
	"&:hover": {
		backgroundColor: "#41B5C3",
		color: "white",
	},
	boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.15)",
};

const MIN_ICON_SIZE = 14;

export default function TransparentDrawer({ mapLocations, setMapLocations, tokenChecked }: Props) {
	const [open, setOpen] = useState(false);

	const logged = useSelector(isLogged);
	const navigate = useNavigate();

	const [params, setParams] = React.useState<any>(INITIAL_PARAMS);

	const [loadingDownload, setLoadingDownload] = React.useState(false);

	const [loading, setLoading] = React.useState(false);
	const [genderOptions, setGenderOptions] = React.useState<any>();
	const [cancelTokenSource, setCancelTokenSource] = React.useState<any>(null);
	const [showConfirm, setShowConfirm] = React.useState(false);

	const handleDrawerToggle = () => setOpen(!open);

	const handleFilter = () => {
		setLoading(true);

		const newCancelTokenSource = axios.CancelToken.source();
		setCancelTokenSource(newCancelTokenSource);

		searchByGenders(params, newCancelTokenSource.token)
			.then(({ result, total, grouped }) => {
				var locations: any[] = [];

				locations = result.map(({ coordinates, extraction, age, ...data }, index) => {
					const total = data.total ?? 0;
					const size = Math.min(Math.max(MIN_ICON_SIZE, total * 0.5), 100);

					return {
						extraction,
						...data,
						...(grouped ? coordinates : extraction.coordinates),
						color: age.color,
						total,
						id: index,
						size,
						iconAnchor: [Math.floor(Math.random() * size) * 0.5, Math.floor(Math.random() * size) * 0.5],
					};
				});

				if (grouped) {
					locations.sort((a, b) => b.total - a.total);
				}

				if (!grouped) {
					locations = utils.separateClosePoints(locations, 15000);
				}

				setMapLocations({ locations, total, grouped });
			})
			.catch((error) => {
				if (!axios.isCancel(error)) {
					toast.error(i18next.t("error"));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleCancel = () => {
		if (cancelTokenSource) {
			cancelTokenSource.cancel("Operation canceled by the user.");
		}
		setLoading(false);
	};

	return (
		<Box sx={{ zIndex: 2, position: "absolute", width: "100%" }}>
			<ConfirmDialog
				title={i18next.t("logout?")}
				handleCancel={() => setShowConfirm(false)}
				handleConfirm={() => {
					navigate(routes.LOGOUT);
					setShowConfirm(false);
				}}
				open={showConfirm}
			/>

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "transparent",
					zIndex: 9999,
					padding: "20px",
					paddingLeft: "35px",
					transition: "margin-right 0.2s",
					marginRight: open ? `${drawerWidth}px` : "0px",
				}}
			>
				<Link onClick={() => logged && setShowConfirm(true)} to={logged ? "" : "." + routes.LOGIN}>
					<IconButton
						sx={[buttonStyle]}
						data-testid={logged ? "logout-button" : "login-button"}
						aria-checked={"true"}
					>
						{logged ? <Logout /> : <Person />}
					</IconButton>
				</Link>

				<div>
					{logged && (
						<Link to={routes.DASHBOARD} onClick={() => setOpen(false)}>
							<IconButton sx={buttonStyle} aria-checked={"true"}>
								<DashboardCustomize />
							</IconButton>
						</Link>
					)}

					<IconButton
						sx={[buttonStyle, { marginRight: 1 }]}
						className="ms-2"
						onClick={handleDrawerToggle}
						aria-checked={"true"}
					>
						{!open ? <FilterAltIcon /> : <Close />}
					</IconButton>
				</div>
			</Box>
			{tokenChecked && (
				<Drawer
					anchor="right"
					open={open}
					variant="persistent"
					onClose={() => setOpen(false)}
					slotProps={{
						paper: {
							sx: {
								zIndex: 1,
								backgroundColor: "white",
								width: `${drawerWidth}px`,
							},
						},
					}}
				>
					<div className="p-4 d-flex flex-column h-100">
						{logged && utils.isAdminOrCollaborator() && (
							<div className="d-flex justify-content-between align-items-center">
								<h4 className="fw-bold text-black m-0">{i18next.t("filters")}</h4>

								<Tooltip title={i18next.t(loadingDownload ? "downloading-result" : "download-result")}>
									{loadingDownload ? (
										<CircularProgress style={{ color: "primary" }} size={15} />
									) : (
										<IconButton
											onClick={() => {
												setLoadingDownload(true);
												searchDownload(params).finally(() => setLoadingDownload(false));
											}}
										>
											<FileDownloadIcon className="text-primary" />
										</IconButton>
									)}
								</Tooltip>
							</div>
						)}
						<div className="pb-4">
							<div className="d-flex gap-3">
								<BasicInput
									label={i18next.t("date-from")}
									value={params.date_from}
									required={true}
									onChange={(e) => setParams({ ...params, date_from: e.target.value })}
									className="w-50"
									type="date"
									datatestid="date-input"
								/>
								<BasicInput
									label={i18next.t("date-to")}
									value={params.date_to}
									required={true}
									onChange={(e) => setParams({ ...params, date_to: e.target.value })}
									className="w-50"
									type="date"
									datatestid="date-input"
								/>
							</div>
							<ExtractionSelect
								multiple={true}
								value={params.extractions}
								onChange={(extractions) => setParams({ ...params, extractions })}
							/>
							<AgeSelect
								multiple={true}
								value={params.ages}
								onChange={(ages) => setParams({ ...params, ages })}
							/>
							<FormationSelect
								multiple={true}
								value={params.formations}
								onChange={(formations) => setParams({ ...params, formations })}
							/>

							<CategorySelect
								multiple={true}
								value={params.categories}
								onChange={(categories) => {
									if (categories.length === 0) {
										setGenderOptions(undefined);
										setParams({
											...params,
											categories: [],
										});
									} else {
										gendersServices
											.search({
												search: "",
												categories: categories.map((category) => category.id),
											})
											.then((res) => {
												setParams({
													...params,
													genders: params.genders.filter((g) =>
														res.result.map((a) => a.id).includes(g.id)
													),
													categories,
												});
												setGenderOptions(res.result);
											});
									}
								}}
							/>
							<GenderSelect
								multiple={true}
								value={params.genders}
								categories={params.categories.map((category) => category.id)}
								options={genderOptions}
								onChange={(genders) => setParams({ ...params, genders })}
							/>
							<BasinSelect
								multiple={true}
								value={params.basins}
								onChange={(basins) => setParams({ ...params, basins })}
							/>

							<div className="d-flex justify-content-center mt-4 pt-2">
								{loading && (
									<Button
										label={i18next.t("cancel")}
										className="me-4"
										secondary
										onClick={handleCancel}
									/>
								)}
								{!loading && (mapLocations || params != INITIAL_PARAMS) && (
									<Button
										label={i18next.t("clear-search")}
										className="me-4"
										secondary
										onClick={() => {
											setParams(INITIAL_PARAMS);
											setMapLocations();
										}}
									/>
								)}
								<Button
									label={i18next.t("filter")}
									loading={loading}
									onClick={handleFilter}
									disabled={loading}
								/>
							</div>
						</div>
					</div>
				</Drawer>
			)}
		</Box>
	);
}
